import React, { useState, useEffect, useCallback, memo } from 'react';
import {
    Calendar, Users, RefreshCw, Search, Filter,
    CheckCircle, XCircle, Clock, AlertCircle, Eye, Edit, Send,
    BarChart3, TrendingUp, PlayCircle, FileText, Target, Save, X,
    UserPlus, Plus, Trash2, AlertTriangle,
    IndianRupee
} from 'lucide-react';

const RecurringInvoicesIntegrationManager = ({ currentOrganization, tokenStatus }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [recurringInvoices, setRecurringInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [selectedInvoices, setSelectedInvoices] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [zohoItems, setZohoItems] = useState([]);
    const [zohoTaxes, setZohoTaxes] = useState([]);
    const [syncProgress, setSyncProgress] = useState(null);
    const [statistics, setStatistics] = useState({});
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [viewingInvoice, setViewingInvoice] = useState(null);
    const [notification, setNotification] = useState(null);

    // Customer creation state
    const [customerCreationMode, setCustomerCreationMode] = useState(false);
    const [customerCreationResult, setCustomerCreationResult] = useState(null);

    // Enhanced recurring invoice form
    const [recurringInvoiceForm, setRecurringInvoiceForm] = useState({
        customer_id: '',
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_gstin: '',
        customer_pan: '',
        customer_billing_address: '',
        customer_shipping_address: '',
        recurrence_name: '',
        invoice_subject: '',
        template_type: 'standard',
        start_date: '',
        end_date: '',
        never_expires: false,
        recurrence_frequency: 'months',
        repeat_every: 1,
        payment_terms: 15,
        payment_terms_label: 'Net 15',
        currency_code: 'INR',
        line_items: [{
            item_id: '',
            item_name: '',
            description: '',
            rate: 0,
            quantity: 1,
            unit: 'qty',
            tax_id: '',
            tax_rate: 18,
            total: 0
        }],
        notes: '',
        terms: '',
        custom_fields: {},
        status: 'active',
        auto_generate: true,
        send_to_customer: false
    });

    const API_BASE_URL = 'https://api.zapbooksai.subraps.com/api';

    const [customerGSTINInfo, setCustomerGSTINInfo] = useState({});
    const [organizationGSTIN, setOrganizationGSTIN] = useState('');

    // Check if user can edit based on approval status
    const canEdit = useCallback((invoice) => {
        const status = invoice.approver_status;
        console.log('Edit check for invoice:', invoice.id, 'status:', status);

        // Only allow editing if explicitly approved or no approval status set (auto-approved)
        return status === 'approved' || !status;
    }, []);

    // Check if user can sync based on approval status  
    const canSync = useCallback((invoice) => {
        const status = invoice.approver_status;
        console.log('Sync check for invoice:', invoice.id, 'status:', status);

        // Only allow syncing if explicitly approved or no approval status set (auto-approved)
        return status === 'approved' || !status;
    }, []);

    // Display approval status
    const getApprovalStatusDisplay = useCallback((invoice) => {
        const status = invoice.approver_status;

        if (status === 'approved') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Approved
                    {invoice.approved_by && (
                        <span className="ml-1">by {invoice.approved_by}</span>
                    )}
                </span>
            );
        }

        if (status === 'rejected') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Rejected
                </span>
            );
        }

        // Default to pending approval
        return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <Clock className="w-3 h-3 mr-1" />
                Pending Approval
            </span>
        );
    }, []);

    // FIXED: Enhanced auth headers with organization support
    const getAuthHeaders = () => {
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        const headers = {
            'Content-Type': 'application/json'
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        // FIXED: Pass organization ID in headers if available
        if (currentOrganization?.organization_id) {
            headers['x-organization-id'] = currentOrganization.organization_id;
            headers['x-company-id'] = currentOrganization.organization_id; // Fallback
        }

        console.log('🔑 Request headers:', headers);
        return headers;
    };

    // Show notification helper
    const showNotification = useCallback((message, type = 'info') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    }, []);

    // Check if organization is available
    const isOrganizationReady = currentOrganization?.organization_id && tokenStatus?.token_valid;

    // Fetch organization GSTIN from zoho_organizations table
    const fetchOrganizationGSTIN = async () => {
        if (!isOrganizationReady) {
            console.warn('Organization not ready, skipping GSTIN fetch');
            return;
        }

        try {
            console.log('Fetching organization GSTIN...');
            const response = await fetch(`${API_BASE_URL}/recurring-invoices/organization-gstin/${currentOrganization.organization_id}`, {
                headers: getAuthHeaders()
            });

            const data = await response.json();
            if (data.success && data.gstin) {
                setOrganizationGSTIN(data.gstin);
                console.log('Fetched organization GSTIN:', data.gstin);
            } else {
                console.warn('No GSTIN found for organization:', currentOrganization.organization_id);
                setOrganizationGSTIN(''); // Set empty if not found
            }
        } catch (error) {
            console.error('Error fetching organization GSTIN:', error);
            showNotification('Failed to fetch organization GSTIN', 'warning');
        }
    };

    // Payment terms options
    const paymentTermsOptions = [
        { value: 0, label: 'Due on Receipt' },
        { value: 7, label: 'Net 7' },
        { value: 15, label: 'Net 15' },
        { value: 30, label: 'Net 30' },
        { value: 45, label: 'Net 45' },
        { value: 60, label: 'Net 60' },
        { value: 90, label: 'Net 90' }
    ];

    // Helper function to format date for HTML input (YYYY-MM-DD)
    const formatDateForInput = useCallback((dateValue) => {
        if (!dateValue) return '';
        try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    }, []);

    // Helper function to extract state code from GSTIN
    const extractStateCodeFromGSTIN = useCallback((gstin) => {
        if (!gstin || gstin.length < 2) return null;
        return gstin.substring(0, 2);
    }, []);

    // Helper function to determine tax type based on GSTIN comparison
    const determineTaxType = useCallback((orgGSTIN, customerGSTIN, gstTreatment = null) => {
        if (!orgGSTIN) {
            return { type: 'IGST', isInterstate: true };
        }

        if (!customerGSTIN) {
            if (gstTreatment) {
                switch (gstTreatment.toLowerCase()) {
                    case 'unregistered':
                    case 'consumer':
                        return { type: 'CGST_SGST', isInterstate: false };
                    case 'overseas':
                    case 'deemed_export':
                        return { type: 'IGST', isInterstate: true };
                    default:
                        return { type: 'IGST', isInterstate: true };
                }
            }
            return { type: 'IGST', isInterstate: true };
        }

        const orgStateCode = extractStateCodeFromGSTIN(orgGSTIN);
        const customerStateCode = extractStateCodeFromGSTIN(customerGSTIN);

        if (gstTreatment) {
            switch (gstTreatment.toLowerCase()) {
                case 'overseas':
                case 'deemed_export':
                    return { type: 'IGST', isInterstate: true };
            }
        }

        if (orgStateCode === customerStateCode) {
            return { type: 'CGST_SGST', isInterstate: false };
        } else {
            return { type: 'IGST', isInterstate: true };
        }
    }, [extractStateCodeFromGSTIN]);

    // FIXED: Fetch customers using organization-aware endpoint
    const fetchCustomers = async () => {
        if (!isOrganizationReady) {
            console.warn('⚠️ Organization not ready, skipping customer fetch');
            return;
        }

        try {
            console.log('Fetching customers from internal customers table...');
            const url = new URL(`${API_BASE_URL}/recurring-invoices/internal-customers`);

            // FIXED: Pass organization_id as query parameter AND in headers
            url.searchParams.append('company_id', currentOrganization.organization_id);

            const response = await fetch(url, {
                headers: getAuthHeaders()
            });
            const data = await response.json();

            if (data.success) {
                setCustomers(data.data || []);
                console.log(`✅ Fetched ${data.data?.length || 0} internal customers`);
            } else {
                throw new Error(data.message || 'Failed to fetch customers');
            }
        } catch (error) {
            console.error('❌ Error fetching customers:', error);
            showNotification('Failed to fetch customers', 'error');
            setCustomers([]);
        }
    };

    // FIXED: Fetch recurring invoices using organization-aware endpoint
    const fetchRecurringInvoices = async () => {
        if (!isOrganizationReady) {
            console.warn('⚠️ Organization not ready, skipping recurring invoices fetch');
            showNotification('Please ensure Zoho Books organization is selected', 'warning');
            return;
        }

        try {
            setLoading(true);
            const url = new URL(`${API_BASE_URL}/recurring-invoices/with-validation-data`);

            // FIXED: Pass organization_id as query parameter AND in headers
            url.searchParams.append('company_id', currentOrganization.organization_id);

            const response = await fetch(url, {
                headers: getAuthHeaders()
            });
            const data = await response.json();

            if (data.success) {
                setRecurringInvoices(data.data || []);
                setStatistics(data.summary || {});
                console.log(`✅ Fetched ${data.data?.length || 0} recurring invoices with validation data`);
                showNotification(`Loaded ${data.data?.length || 0} recurring invoices`, 'success');
            } else {
                throw new Error(data.message || 'Failed to fetch recurring invoices');
            }
        } catch (error) {
            console.error('❌ Error fetching recurring invoices:', error);
            showNotification('Failed to fetch recurring invoices', 'error');
            setRecurringInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch Zoho items
    const fetchZohoItems = async () => {
        if (!isOrganizationReady) {
            console.warn('⚠️ Organization not ready, skipping Zoho items fetch');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/recurring-invoices/zoho/items`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();

            if (data.success) {
                setZohoItems(data.data || []);
                console.log(`✅ Fetched ${data.data?.length || 0} Zoho items`);
            }
        } catch (error) {
            console.error('❌ Error fetching Zoho items:', error);
            showNotification('Failed to fetch Zoho items', 'warning');
        }
    };

    // Fetch Zoho taxes
    const fetchZohoTaxes = async () => {
        if (!isOrganizationReady) {
            console.warn('⚠️ Organization not ready, skipping Zoho taxes fetch');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/recurring-invoices/zoho/taxes`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();

            if (data.success) {
                setZohoTaxes(data.data || {});
                console.log('✅ Fetched Zoho tax rates');
            }
        } catch (error) {
            console.error('❌ Error fetching Zoho taxes:', error);
            showNotification('Failed to fetch tax rates', 'warning');
        }
    };

    // ENHANCED: Better customer search with multiple criteria
    const findExistingCustomerByName = useCallback((customerName) => {
        if (!customerName || !customers.length) return null;

        // First, try exact match on display_name or company_name
        let customer = customers.find(c =>
            c.company_name === customerName ||
            c.display_name === customerName
        );

        // If not found, try case-insensitive match
        if (!customer) {
            customer = customers.find(c =>
                c.company_name?.toLowerCase() === customerName.toLowerCase() ||
                c.display_name?.toLowerCase() === customerName.toLowerCase()
            );
        }

        // If still not found, try partial match
        if (!customer) {
            customer = customers.find(c =>
                c.company_name?.toLowerCase().includes(customerName.toLowerCase()) ||
                c.display_name?.toLowerCase().includes(customerName.toLowerCase())
            );
        }

        return customer;
    }, [customers]);

    // UPDATED: Enhanced customer name analysis with better error handling
    const handleCustomerNameAnalysis = useCallback((customerName) => {
        if (!customerName) {
            setCustomerCreationMode(false);
            setCustomerGSTINInfo({});
            return;
        }

        const existingCustomer = findExistingCustomerByName(customerName);

        if (existingCustomer) {
            console.log('✅ Found existing customer by name:', existingCustomer);

            // Update form with existing customer data
            setRecurringInvoiceForm(prevForm => ({
                ...prevForm,
                customer_id: existingCustomer.id,
                customer_name: existingCustomer.display_name,
                customer_email: existingCustomer.email_address || '',
                customer_phone: existingCustomer.phone || '',
                customer_gstin: existingCustomer.gstin_uin || '',
                customer_pan: existingCustomer.pan || '',
                customer_billing_address: existingCustomer.billing_address || '',
                customer_shipping_address: existingCustomer.shipping_address || ''
            }));

            setCustomerGSTINInfo({
                gst_no: existingCustomer.gstin_uin,
                gst_treatment: existingCustomer.gst_treatment,
                place_of_contact: existingCustomer.place_of_supply
            });

            setCustomerCreationMode(false);
            showNotification(`Found existing customer: ${existingCustomer.display_name}`, 'success');
        } else {
            console.log('⚠️ Customer not found, entering creation mode');

            // Clear customer fields but keep the name
            setRecurringInvoiceForm(prevForm => ({
                ...prevForm,
                customer_id: '',
                customer_email: '',
                customer_phone: '',
                customer_gstin: '',
                customer_pan: '',
                customer_billing_address: '',
                customer_shipping_address: ''
            }));

            setCustomerGSTINInfo({});
            setCustomerCreationMode(true);
            showNotification(`New customer will be created: ${customerName}`, 'info');
        }
    }, [findExistingCustomerByName, showNotification]);

    // Form field change handler
    const handleFormFieldChange = useCallback((field, value) => {
        setRecurringInvoiceForm(prevForm => ({
            ...prevForm,
            [field]: value || '' // Ensure no null values
        }));

        if (field === 'customer_name') {
            handleCustomerNameAnalysis(value);
        }

        // Update GSTIN info when customer GSTIN changes
        if (field === 'customer_gstin') {
            setCustomerGSTINInfo(prev => ({
                ...prev,
                gst_no: value || ''
            }));
        }
    }, [handleCustomerNameAnalysis]);

    // Line item change handler
    const handleLineItemChange = useCallback((index, field, value) => {
        setRecurringInvoiceForm(prevForm => {
            const newLineItems = [...prevForm.line_items];
            newLineItems[index] = {
                ...newLineItems[index],
                [field]: value
            };

            if (field === 'quantity' || field === 'rate') {
                const quantity = field === 'quantity' ? parseFloat(value) || 0 : newLineItems[index].quantity;
                const rate = field === 'rate' ? parseFloat(value) || 0 : newLineItems[index].rate;
                newLineItems[index].total = quantity * rate;
            }

            return {
                ...prevForm,
                line_items: newLineItems
            };
        });
    }, []);

    // Zoho item selection handler
    const handleZohoItemSelect = useCallback((index, itemId) => {
        const selectedItem = zohoItems.find(zi => zi.item_id === itemId);

        setRecurringInvoiceForm(prevForm => {
            const newLineItems = [...prevForm.line_items];
            newLineItems[index] = {
                ...newLineItems[index],
                item_id: itemId,
                item_name: selectedItem?.name || '',
                description: selectedItem?.name || selectedItem?.description || newLineItems[index].description,
                rate: selectedItem?.rate || newLineItems[index].rate,
                unit: selectedItem?.unit || newLineItems[index].unit
            };

            return {
                ...prevForm,
                line_items: newLineItems
            };
        });
    }, [zohoItems]);

    // Tax rate change handler
    const handleTaxRateChange = useCallback((e, index) => {
        const selectedRate = e.target.value ? parseFloat(e.target.value) : 0;

        setRecurringInvoiceForm(prevForm => {
            const newLineItems = [...prevForm.line_items];
            newLineItems[index] = {
                ...newLineItems[index],
                tax_rate: selectedRate,
                tax_id: selectedRate > 0 ? '' : ''
            };

            return {
                ...prevForm,
                line_items: newLineItems
            };
        });
    }, []);

    // Add/Remove line item handlers
    const addLineItem = useCallback(() => {
        setRecurringInvoiceForm(prevForm => ({
            ...prevForm,
            line_items: [
                ...prevForm.line_items,
                {
                    item_id: '',
                    item_name: '',
                    description: '',
                    rate: 0,
                    quantity: 1,
                    unit: 'qty',
                    tax_id: '',
                    tax_rate: 18,
                    total: 0
                }
            ]
        }));
    }, []);

    const removeLineItem = useCallback((index) => {
        setRecurringInvoiceForm(prevForm => ({
            ...prevForm,
            line_items: prevForm.line_items.filter((_, i) => i !== index)
        }));
    }, []);

    // Get available tax rates based on customer GSTIN
    const getAvailableTaxRates = useCallback(() => {
        if (!zohoTaxes || !zohoTaxes.organized_taxes) {
            return [
                { value: '', label: 'Non-Taxable (0%)' },
                { value: '5', label: 'GST 5%' },
                { value: '12', label: 'GST 12%' },
                { value: '18', label: 'GST 18%' },
                { value: '28', label: 'GST 28%' }
            ];
        }

        const taxInfo = determineTaxType(
            organizationGSTIN,
            customerGSTINInfo.gst_no,
            customerGSTINInfo.gst_treatment
        );

        const rates = [{ value: '', label: 'Non-Taxable (0%)' }];
        const commonRates = [5, 12, 18, 28];

        commonRates.forEach(rate => {
            if (taxInfo.isInterstate) {
                rates.push({
                    value: rate.toString(),
                    label: `IGST ${rate}% (Interstate)`,
                    taxType: 'igst'
                });
            } else {
                const halfRate = rate / 2;
                rates.push({
                    value: rate.toString(),
                    label: `CGST ${halfRate}% + SGST ${halfRate}% (Intrastate)`,
                    taxType: 'cgst_sgst'
                });
            }
        });

        return rates;
    }, [zohoTaxes, organizationGSTIN, customerGSTINInfo, determineTaxType]);

    // Calculate totals for line items
    const calculateTotals = useCallback((lineItems) => {
        let subTotal = 0;
        let taxTotal = 0;

        lineItems.forEach(item => {
            const itemTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0);
            subTotal += itemTotal;

            if (item.tax_rate) {
                const taxAmount = (itemTotal * parseFloat(item.tax_rate)) / 100;
                taxTotal += taxAmount;
            }
        });

        return {
            subTotal: parseFloat(subTotal.toFixed(2)),
            taxTotal: parseFloat(taxTotal.toFixed(2)),
            totalAmount: parseFloat((subTotal + taxTotal).toFixed(2))
        };
    }, []);

    // Enhanced validation for recurring invoices (matches backend logic)
    const validateRecurringInvoice = useCallback((invoice) => {
        const missingFields = [];
        const warnings = [];

        if (!invoice.customer_name) missingFields.push('Customer Name');
        if (!invoice.recurrence_name) missingFields.push('Profile Name');
        if (!invoice.start_date) missingFields.push('Start Date');
        if (!invoice.recurrence_frequency) missingFields.push('Frequency');
        if (!invoice.line_items || invoice.line_items.length === 0) missingFields.push('Line Items');

        if (invoice.start_date && invoice.end_date) {
            const start = new Date(invoice.start_date);
            const end = new Date(invoice.end_date);
            if (start >= end) {
                warnings.push('End date should be after start date');
            }
        }

        if (invoice.line_items) {
            invoice.line_items.forEach((item, index) => {
                if (!item.description && !item.item_name) missingFields.push(`Line Item ${index + 1} Description`);
                if (!item.rate || item.rate <= 0) missingFields.push(`Line Item ${index + 1} Rate`);
                if (!item.quantity || item.quantity <= 0) missingFields.push(`Line Item ${index + 1} Quantity`);
            });
        }

        const completionPercentage = Math.round(
            ((8 - missingFields.length) / 8) * 100
        );

        return {
            isValid: missingFields.length === 0,
            missingFields,
            warnings,
            completionPercentage,
            canSync: missingFields.length === 0 && warnings.length === 0
        };
    }, []);


    // UPDATED: Enhanced loadInvoiceForEdit with better customer handling
    const loadInvoiceForEdit = useCallback(async (invoice) => {
        setEditingInvoice(invoice);

        // Format the invoice data with null safety
        const formattedInvoice = {
            ...invoice,
            start_date: formatDateForInput(invoice.start_date),
            end_date: formatDateForInput(invoice.end_date),
            never_expires: !invoice.end_date,
            // FIXED: Ensure all fields have default values not null
            customer_email: invoice.customer_email || invoice.customer_email_from_db || '',
            customer_phone: invoice.customer_phone || invoice.customer_phone_from_db || '',
            customer_gstin: invoice.customer_gstin || invoice.customer_gstin_from_db || '',
            customer_pan: invoice.customer_pan || invoice.customer_pan_from_db || '',
            customer_billing_address: invoice.customer_billing_address || invoice.customer_billing_address_from_db || '',
            customer_shipping_address: invoice.customer_shipping_address || invoice.customer_shipping_address_from_db || '',
            line_items: invoice.line_items && invoice.line_items.length > 0
                ? invoice.line_items
                : [{
                    item_id: '',
                    item_name: '',
                    description: '',
                    rate: 0,
                    quantity: 1,
                    unit: 'qty',
                    tax_id: '',
                    tax_rate: 18,
                    total: 0
                }]
        };

        // FIXED: Better customer handling with multiple fallback strategies
        if (invoice.customer_id) {
            console.log('Looking for customer ID', invoice.customer_id, 'in', customers.length, 'customers');

            // Wait for customers to load if they're not ready yet
            if (customers.length === 0 && isOrganizationReady) {
                console.log('Customers not loaded yet, fetching...');
                try {
                    await fetchCustomers();
                    console.log('Customers fetched for editing');
                } catch (error) {
                    console.error('Failed to fetch customers for editing:', error);
                }
            }

            // Try to find customer in the customers array
            let existingCustomer = customers.find(c => c.id == invoice.customer_id);

            if (existingCustomer) {
                console.log('Loading existing customer details:', existingCustomer.display_name);

                // PRIORITY: Use customer record data over recurring invoice stored data
                formattedInvoice.customer_email = existingCustomer.email_address || formattedInvoice.customer_email;
                formattedInvoice.customer_phone = existingCustomer.phone || formattedInvoice.customer_phone;
                formattedInvoice.customer_gstin = existingCustomer.gstin_uin || formattedInvoice.customer_gstin;
                formattedInvoice.customer_pan = existingCustomer.pan || formattedInvoice.customer_pan;
                formattedInvoice.customer_billing_address = existingCustomer.billing_address || formattedInvoice.customer_billing_address;
                formattedInvoice.customer_shipping_address = existingCustomer.shipping_address || formattedInvoice.customer_shipping_address;

                // Set customer GSTIN info for tax calculations
                setCustomerGSTINInfo({
                    gst_no: existingCustomer.gstin_uin || '',
                    gst_treatment: existingCustomer.gst_treatment || '',
                    place_of_contact: existingCustomer.place_of_supply || ''
                });

                setCustomerCreationMode(false);
                showNotification(`Loaded customer details: ${existingCustomer.display_name}`, 'success');
            } else if (invoice.customer_name) {
                // FALLBACK: Try to find customer by name
                const customerByName = findExistingCustomerByName(invoice.customer_name);
                if (customerByName) {
                    console.log('Found customer by name fallback:', customerByName.display_name);

                    // Use the found customer and update the invoice's customer_id
                    formattedInvoice.customer_id = customerByName.id;
                    formattedInvoice.customer_email = customerByName.email_address || formattedInvoice.customer_email;
                    formattedInvoice.customer_phone = customerByName.phone || formattedInvoice.customer_phone;
                    formattedInvoice.customer_gstin = customerByName.gstin_uin || formattedInvoice.customer_gstin;
                    formattedInvoice.customer_pan = customerByName.pan || formattedInvoice.customer_pan;
                    formattedInvoice.customer_billing_address = customerByName.billing_address || formattedInvoice.customer_billing_address;
                    formattedInvoice.customer_shipping_address = customerByName.shipping_address || formattedInvoice.customer_shipping_address;

                    setCustomerGSTINInfo({
                        gst_no: customerByName.gstin_uin || '',
                        gst_treatment: customerByName.gst_treatment || '',
                        place_of_contact: customerByName.place_of_supply || ''
                    });

                    setCustomerCreationMode(false);
                    showNotification(`Found customer by name: ${customerByName.display_name}`, 'success');
                } else {
                    // Customer not found - use stored data from recurring invoice
                    console.log('Customer not found by ID or name, using stored data from recurring invoice');
                    setCustomerCreationMode(false);
                    setCustomerGSTINInfo({
                        gst_no: formattedInvoice.customer_gstin || '',
                        gst_treatment: '',
                        place_of_contact: ''
                    });
                    showNotification(`Using stored customer data for: ${invoice.customer_name}`, 'info');
                }
            }
        } else if (invoice.customer_name) {
            // No customer ID - try to find by name
            handleCustomerNameAnalysis(invoice.customer_name);
        } else {
            setCustomerCreationMode(true);
            setCustomerGSTINInfo({});
        }

        setRecurringInvoiceForm(formattedInvoice);
    }, [formatDateForInput, customers, handleCustomerNameAnalysis, showNotification, findExistingCustomerByName, fetchCustomers, isOrganizationReady]);

    const handleEditClick = async (invoice) => {
        // Check if invoice can be edited
        if (!canEdit(invoice)) {
            const status = invoice.approver_status;
            let message = 'This invoice cannot be edited - ';

            if (status === 'pending_approval') {
                message += 'pending approval';
            } else if (status === 'rejected') {
                message += 'rejected';
            } else {
                message += 'not approved';
            }

            showNotification(message, 'warning');
            return;
        }

        // Check if invoice is synced and prevent editing
        if (invoice.zoho_recurring_invoice_id) {
            showNotification('This recurring invoice has already been synced to Zoho Books and cannot be edited to maintain data consistency.', 'warning');
            return;
        }

        try {
            setLoading(true);

            // FIXED: Always ensure customers are loaded before editing
            if (customers.length === 0 && isOrganizationReady) {
                console.log('Pre-loading customers for edit...');
                await fetchCustomers();
                console.log('Customers pre-loaded successfully');
                // Small delay to ensure state updates
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            loadInvoiceForEdit(invoice);
            setActiveTab('edit');
        } catch (error) {
            console.error('Failed to prepare edit:', error);
            showNotification('Failed to load customer data. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // FIXED: Update recurring invoice with enhanced customer handling and organization context
    const updateRecurringInvoice = async () => {
        if (!isOrganizationReady) {
            showNotification('Organization not ready. Please ensure Zoho Books organization is selected.', 'error');
            return;
        }

        try {
            setLoading(true);
            setCustomerCreationResult(null);

            const formData = {
                ...recurringInvoiceForm,
                start_date: recurringInvoiceForm.start_date || null,
                end_date: recurringInvoiceForm.never_expires ? null : (recurringInvoiceForm.end_date || null),
                // FIXED: Always pass current organization ID
                company_id: currentOrganization.organization_id,
                organization_id: currentOrganization.organization_id, // Also pass as organization_id
                user_access_id: currentOrganization.user_access_id || null
            };

            console.log('📤 Updating recurring invoice with organization context:', {
                invoice_id: editingInvoice.id,
                organization_id: currentOrganization.organization_id,
                customer_mode: customerCreationMode,
                has_customer_id: !!formData.customer_id,
                customer_name: formData.customer_name
            });

            const response = await fetch(`${API_BASE_URL}/recurring-invoices/${editingInvoice.id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (result.success) {
                if (result.customer_action) {
                    setCustomerCreationResult(result.customer_action);

                    if (result.customer_action.success) {
                        const actionText = result.customer_action.action === 'new_customer_created' ? 'created' : 'found';
                        showNotification(`Customer ${actionText} successfully: ${result.customer_action.message}`, 'success');
                    } else {
                        showNotification(`Customer handling failed: ${result.customer_action.error}`, 'error');
                    }
                }

                showNotification('Recurring invoice updated successfully!', 'success');

                await Promise.all([
                    fetchRecurringInvoices(),
                    fetchCustomers()
                ]);

                setEditingInvoice(null);
                setActiveTab('manage');
            } else {
                throw new Error(result.message || 'Failed to update recurring invoice');
            }

        } catch (error) {
            console.error('❌ Error updating recurring invoice:', error);
            showNotification(`Error: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // UPDATED: Enhanced sync to Zoho Books with detailed error handling
    const syncRecurringInvoices = async (invoiceIds = null) => {
        if (!isOrganizationReady) {
            showNotification('Organization not ready. Please ensure Zoho Books organization is selected.', 'error');
            return;
        }

        try {
            setLoading(true);
            setSyncProgress({ current: 0, total: 0, status: 'Preparing sync...' });

            const invoicesToSync = invoiceIds || selectedInvoices;

            // Check approval status for each invoice before processing
            for (const id of invoicesToSync) {
                const invoice = recurringInvoices.find(inv => inv.id === id);

                if (!canSync(invoice)) {
                    const status = invoice.approver_status;
                    let message = `Cannot sync invoice "${invoice.recurrence_name}" - `;

                    if (status === 'pending_approval') {
                        message += 'pending approval';
                    } else if (status === 'rejected') {
                        message += 'rejected';
                    } else {
                        message += 'not approved';
                    }

                    showNotification(message, 'warning');
                    return;
                }
            }

            if (invoicesToSync.length === 0) {
                showNotification('Please select recurring invoices to sync', 'warning');
                return;
            }

            // Prepare sync data with validation (matches backend expectations)
            const syncData = [];
            const invalidInvoices = [];

            for (const id of invoicesToSync) {
                const invoice = recurringInvoices.find(inv => inv.id === id);
                const validation = validateRecurringInvoice(invoice);

                if (!validation.canSync) {
                    invalidInvoices.push(`${invoice.recurrence_name}: ${validation.missingFields.join(', ')}`);
                    continue;
                }

                syncData.push({
                    recurring_invoice_id: invoice.id,
                    customer_id: invoice.customer_id,
                    customer_name: invoice.customer_name,
                    recurrence_name: invoice.recurrence_name,
                    invoice_subject: invoice.invoice_subject,
                    start_date: invoice.start_date,
                    end_date: invoice.end_date,
                    recurrence_frequency: invoice.recurrence_frequency,
                    repeat_every: invoice.repeat_every || 1,
                    payment_terms: invoice.payment_terms || 15,
                    payment_terms_label: invoice.payment_terms_label || 'Net 15',
                    line_items: invoice.line_items.map(item => ({
                        item_id: item.item_id || '',
                        item_name: item.item_name || item.description,
                        description: item.description || item.item_name,
                        rate: parseFloat(item.rate),
                        quantity: parseFloat(item.quantity),
                        unit: item.unit || 'qty',
                        tax_rate: parseFloat(item.tax_rate || 0),
                        tax_id: item.tax_id || ''
                    })),
                    notes: invoice.notes || '',
                    terms: invoice.terms || ''
                });
            }

            if (invalidInvoices.length > 0) {
                showNotification(
                    `Cannot sync ${invalidInvoices.length} invoice(s). Missing required fields: ${invalidInvoices.join('; ')}`,
                    'error'
                );
                if (syncData.length === 0) return;
            }

            setSyncProgress({
                current: 0,
                total: syncData.length,
                status: 'Starting sync...'
            });

            console.log('📤 Starting sync with organization context:', {
                organization_id: currentOrganization.organization_id,
                invoices_count: syncData.length
            });

            // CORRECTED: Use 'recurring_invoice_updates' instead of 'recurringInvoiceUpdates'
            const response = await fetch(`${API_BASE_URL}/recurring-invoices/sync-to-zoho`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    recurring_invoice_updates: syncData,
                    company_id: currentOrganization.organization_id,
                    organization_id: currentOrganization.organization_id
                })
            });

            const result = await response.json();

            if (result.success) {
                console.log('✅ Sync completed:', result.summary);
                await fetchRecurringInvoices();
                setSelectedInvoices([]);

                // ENHANCED: Show detailed success/failure breakdown
                const successCount = result.summary.successful || 0;
                const failedCount = result.summary.failed || 0;
                const successRate = result.summary.success_rate || 0;

                if (failedCount > 0) {
                    // Show detailed error information
                    const failedResults = result.results.filter(r => !r.success);
                    const errorDetails = failedResults.map(r =>
                        `• ${r.recurring_invoice_id}: ${r.error}`
                    ).join('\n');

                    showNotification(
                        `Sync completed with ${failedCount} failures:\n${errorDetails}\n\nSuccessful: ${successCount}, Success rate: ${successRate}%`,
                        'error'
                    );

                    // Also log detailed errors for debugging
                    console.error('❌ Detailed sync failures:', failedResults);
                } else {
                    showNotification(
                        `All ${successCount} invoices synced successfully! (${successRate}% success rate)`,
                        'success'
                    );
                }

                // ENHANCED: Show individual invoice status updates
                if (result.results && result.results.length > 0) {
                    const statusUpdates = result.results.map(r => {
                        const invoice = recurringInvoices.find(inv => inv.id === r.recurring_invoice_id);
                        return {
                            name: invoice?.recurrence_name || `Invoice ${r.recurring_invoice_id}`,
                            status: r.success ? 'Success' : 'Failed',
                            error: r.error || null,
                            zoho_id: r.zoho_recurring_invoice_id || null
                        };
                    });

                    console.log('📊 Individual sync results:', statusUpdates);
                }

            } else {
                // ENHANCED: Handle API-level errors with more detail
                const errorMessage = result.message || 'Sync failed';
                const errorDetails = result.error || 'Unknown error occurred';

                throw new Error(`${errorMessage}: ${errorDetails}`);
            }

        } catch (error) {
            console.error('❌ Sync error:', error);

            // ENHANCED: More detailed error messages
            let errorMessage = 'Sync failed';

            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                errorMessage = 'Network error: Could not connect to server. Please check your connection.';
            } else if (error.message.includes('401')) {
                errorMessage = 'Authentication error: Please check your Zoho Books token.';
            } else if (error.message.includes('403')) {
                errorMessage = 'Permission error: Insufficient permissions for Zoho Books API.';
            } else if (error.message.includes('429')) {
                errorMessage = 'Rate limit exceeded: Please wait a moment and try again.';
            } else if (error.message.includes('500')) {
                errorMessage = 'Server error: Please try again later or contact support.';
            } else {
                errorMessage = `Sync failed: ${error.message}`;
            }

            showNotification(errorMessage, 'error');
        } finally {
            setLoading(false);
            setSyncProgress(null);
        }
    };

    // UPDATED: Enhanced useEffect to ensure customers are loaded before editing
    useEffect(() => {
        if (isOrganizationReady) {
            const initializeComponent = async () => {
                try {
                    console.log('🚀 Initializing Recurring Invoices with organization:', currentOrganization.organization_id);

                    // FIXED: Ensure customers are loaded before other operations
                    await fetchCustomers();

                    await Promise.all([
                        fetchRecurringInvoices(),
                        fetchZohoItems(),
                        fetchZohoTaxes(),
                        fetchOrganizationGSTIN()
                    ]);
                } catch (error) {
                    console.error('❌ Failed to initialize component:', error);
                    showNotification('Failed to initialize application', 'error');
                }
            };
            initializeComponent();
        } else {
            console.log('⏳ Waiting for organization to be ready...');
            if (currentOrganization && !currentOrganization.organization_id) {
                showNotification('Organization ID is missing', 'error');
            }
            if (tokenStatus && !tokenStatus.token_valid) {
                showNotification('Zoho Books token is invalid', 'error');
            }
        }
    }, [isOrganizationReady, currentOrganization?.organization_id]);

    // Filter and search logic
    const filteredInvoices = recurringInvoices.filter(invoice => {
        const matchesSearch = invoice.recurrence_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            invoice.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filterStatus === 'all' ||
            (filterStatus === 'active' && invoice.status === 'active') ||
            (filterStatus === 'inactive' && invoice.status === 'inactive') ||
            (filterStatus === 'synced' && invoice.zoho_recurring_invoice_id) ||
            (filterStatus === 'pending' && !invoice.zoho_recurring_invoice_id);

        return matchesSearch && matchesFilter;
    });

    // Organization Status Banner
    const OrganizationStatusBanner = () => {
        if (isOrganizationReady) {
            return (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                        <div>
                            <h3 className="text-green-800 font-medium">Organization Ready</h3>
                            <p className="text-green-700 text-sm mt-1">
                                Connected to: <strong>{currentOrganization.name}</strong> ({currentOrganization.organization_id})
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-yellow-500 mr-2" />
                    <div>
                        <h3 className="text-yellow-800 font-medium">Organization Setup Required</h3>
                        <p className="text-yellow-700 text-sm mt-1">
                            {!currentOrganization ? 'No organization selected' :
                                !currentOrganization.organization_id ? 'Organization ID is missing' :
                                    !tokenStatus?.token_valid ? 'Invalid Zoho Books token' :
                                        'Please ensure Zoho Books is properly connected'}
                        </p>
                        <p className="text-yellow-700 text-sm mt-1">
                            Please go to the Books Overview tab to select your organization.
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    // FIXED: Enhanced customer creation fields with null safety
    const CustomerCreationFields = memo(({ formData, onChange, isNewCustomer }) => (
        <div className="space-y-4 p-4 border border-teal-200 rounded-lg bg-teal-50">
            <h4 className="text-sm font-semibold text-teal-800 flex items-center">
                <UserPlus className="w-4 h-4 mr-2" />
                {isNewCustomer ? 'New Customer Details' : 'Customer Details'}
                {isNewCustomer && (
                    <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                        Will be created
                    </span>
                )}
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                    <input
                        type="text"
                        value={formData.customer_name || ''}
                        onChange={(e) => onChange('customer_name', e.target.value)}
                        placeholder="Company or individual name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                        type="email"
                        value={formData.customer_email || ''}
                        onChange={(e) => onChange('customer_email', e.target.value)}
                        placeholder="customer@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                        type="tel"
                        value={formData.customer_phone || ''}
                        onChange={(e) => onChange('customer_phone', e.target.value)}
                        placeholder="Phone number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                    <input
                        type="text"
                        value={formData.customer_gstin || ''}
                        onChange={(e) => onChange('customer_gstin', e.target.value)}
                        placeholder="GST identification number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        maxLength="15"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                    <input
                        type="text"
                        value={formData.customer_pan || ''}
                        onChange={(e) => onChange('customer_pan', e.target.value)}
                        placeholder="PAN number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        maxLength="10"
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Address</label>
                <textarea
                    value={formData.customer_billing_address || ''}
                    onChange={(e) => onChange('customer_billing_address', e.target.value)}
                    placeholder="Complete billing address"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
            </div>

            {formData.customer_gstin && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                    <div className="flex items-center justify-between">
                        <span>
                            <strong>Tax Type:</strong> {determineTaxType(organizationGSTIN, formData.customer_gstin).type}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs ${determineTaxType(organizationGSTIN, formData.customer_gstin).isInterstate
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-green-100 text-green-800'
                            }`}>
                            {determineTaxType(organizationGSTIN, formData.customer_gstin).isInterstate ? 'Interstate' : 'Intrastate'}
                        </span>
                    </div>
                </div>
            )}
        </div>
    ));

    // Line Item Row Component
    const LineItemRow = memo(({ item, index, onItemChange, onZohoItemSelect, onTaxChange, onRemove, canRemove }) => (
        <div className="border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Item/Description *</label>
                    <div className="space-y-2">
                        <select
                            value={item.item_id}
                            onChange={(e) => onZohoItemSelect(index, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        >
                            <option value="">Select from Zoho Items</option>
                            {zohoItems.map(zohoItem => (
                                <option key={zohoItem.item_id} value={zohoItem.item_id}>
                                    {zohoItem.name} - ₹{zohoItem.rate}
                                </option>
                            ))}
                        </select>
                        <input
                            type="text"
                            value={item.description || item.item_name}
                            onChange={(e) => onItemChange(index, 'description', e.target.value)}
                            placeholder="Item description"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                    <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => onItemChange(index, 'quantity', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rate *</label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.rate}
                        onChange={(e) => onItemChange(index, 'rate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                    <select
                        value={item.unit || 'qty'}
                        onChange={(e) => onItemChange(index, 'unit', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                        <option value="qty">Quantity</option>
                        <option value="pcs">Pieces</option>
                        <option value="hrs">Hours</option>
                        <option value="days">Days</option>
                        <option value="months">Months</option>
                        <option value="kg">Kilograms</option>
                        <option value="box">Box</option>
                        <option value="set">Set</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
                    <select
                        value={item.tax_rate || ''}
                        onChange={(e) => onTaxChange(e, index)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                        {getAvailableTaxRates().map((option, idx) => (
                            <option key={`tax-${idx}`} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 font-medium">
                        ₹{((item.quantity || 0) * (item.rate || 0)).toLocaleString()}
                    </div>
                    {item.tax_rate > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                            Tax: ₹{(((item.quantity || 0) * (item.rate || 0) * (item.tax_rate || 0)) / 100).toLocaleString()}
                        </div>
                    )}
                </div>
                <div className="flex items-end">
                    {canRemove && (
                        <button
                            onClick={() => onRemove(index)}
                            className="text-red-600 hover:text-red-700 p-2"
                            title="Remove Line Item"
                            type="button"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    ));

    // FIXED: Enhanced notification component with multiline support
    const NotificationBanner = () => {
        if (!notification) return null;

        const bgColor = notification.type === 'success' ? 'bg-green-50 border-green-200' :
            notification.type === 'error' ? 'bg-red-50 border-red-200' :
                notification.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                    'bg-blue-50 border-blue-200';

        const textColor = notification.type === 'success' ? 'text-green-800' :
            notification.type === 'error' ? 'text-red-800' :
                notification.type === 'warning' ? 'text-yellow-800' :
                    'text-blue-800';

        const Icon = notification.type === 'success' ? CheckCircle :
            notification.type === 'error' ? XCircle :
                notification.type === 'warning' ? AlertTriangle :
                    AlertCircle;

        return (
            <div className={`mb-4 p-3 rounded-lg border ${bgColor}`}>
                <div className="flex items-start">
                    <Icon className={`w-5 h-5 ${textColor} mr-2 mt-0.5 flex-shrink-0`} />
                    <div className="flex-1">
                        {/* FIXED: Handle multiline error messages properly */}
                        {notification.message && notification.message.includes('\n') ? (
                            <div className={`text-sm font-medium ${textColor}`}>
                                {notification.message.split('\n').map((line, index) => (
                                    <div key={index} className={index > 0 ? 'mt-1' : ''}>
                                        {line}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <span className={`text-sm font-medium ${textColor}`}>
                                {notification.message || 'Notification'}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={() => setNotification(null)}
                        className={`ml-2 ${textColor} hover:opacity-75 flex-shrink-0`}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    };

    // Overview Dashboard
    const OverviewDashboard = () => {
        const activeInvoices = recurringInvoices.filter(inv => inv.status === 'active');
        const syncedInvoices = recurringInvoices.filter(inv => inv.zoho_recurring_invoice_id);
        const readyToSync = recurringInvoices.filter(inv => {
            const validation = validateRecurringInvoice(inv);
            return validation.canSync && !inv.zoho_recurring_invoice_id;
        });

        const totalMonthlyRevenue = activeInvoices.reduce((sum, invoice) => {
            if (invoice.line_items && invoice.line_items.length > 0) {
                const totals = calculateTotals(invoice.line_items);
                const multiplier = invoice.recurrence_frequency === 'months' ? 1 / (invoice.repeat_every || 1) : 1;
                return sum + (totals.totalAmount * multiplier);
            }
            return sum;
        }, 0);

        return (
            <div className="space-y-6">
                <OrganizationStatusBanner />

                {isOrganizationReady && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="bg-white rounded-lg border p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Total Recurring Invoices</p>
                                        <p className="text-2xl font-bold text-gray-900">{recurringInvoices.length}</p>
                                    </div>
                                    <FileText className="w-8 h-8 text-blue-600" />
                                </div>
                                <div className="mt-4 flex items-center">
                                    <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                                    <span className="text-sm text-green-600">Ready to sync: {readyToSync.length}</span>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg border p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Active Invoices</p>
                                        <p className="text-2xl font-bold text-green-600">{activeInvoices.length}</p>
                                    </div>
                                    <PlayCircle className="w-8 h-8 text-green-600" />
                                </div>
                                <div className="mt-4 flex items-center">
                                    <Clock className="w-4 h-4 text-blue-500 mr-1" />
                                    <span className="text-sm text-blue-600">
                                        Monthly: {activeInvoices.filter(inv => inv.recurrence_frequency === 'months').length}
                                    </span>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg border p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Synced to Zoho</p>
                                        <p className="text-2xl font-bold text-teal-600">{syncedInvoices.length}</p>
                                    </div>
                                    <CheckCircle className="w-8 h-8 text-teal-600" />
                                </div>
                                <div className="mt-4 flex items-center">
                                    <Target className="w-4 h-4 text-teal-500 mr-1" />
                                    <span className="text-sm text-teal-600">
                                        Success rate: {recurringInvoices.length > 0 ? Math.round((syncedInvoices.length / recurringInvoices.length) * 100) : 0}%
                                    </span>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg border p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
                                        <p className="text-2xl font-bold text-purple-600">
                                            ₹{totalMonthlyRevenue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                        </p>
                                    </div>
                                    <IndianRupee className="w-8 h-8 text-purple-600" />
                                </div>
                                <div className="mt-4 flex items-center">
                                    <BarChart3 className="w-4 h-4 text-purple-500 mr-1" />
                                    <span className="text-sm text-purple-600">
                                        Annual: ₹{(totalMonthlyRevenue * 12).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-white rounded-lg border p-6">
                            <h3 className="text-lg font-semibold mb-4">Recent Recurring Invoices</h3>
                            <div className="space-y-3">
                                {filteredInvoices.slice(0, 5).map(invoice => {
                                    const validation = validateRecurringInvoice(invoice);
                                    const totals = calculateTotals(invoice.line_items || []);

                                    return (
                                        <div key={invoice.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                                            <div className="flex items-center space-x-3">
                                                <div className={`w-3 h-3 rounded-full ${invoice.zoho_recurring_invoice_id ? 'bg-green-500' :
                                                    validation.isValid ? 'bg-yellow-500' : 'bg-red-500'
                                                    }`} />
                                                <div>
                                                    <p className="font-medium">{invoice.recurrence_name}</p>
                                                    <p className="text-sm text-gray-600">
                                                        {invoice.customer_name} • Every {invoice.repeat_every || 1} {invoice.recurrence_frequency}
                                                    </p>
                                                    {invoice.is_new_customer && (
                                                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                                                            New Customer
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span className={`px-2 py-1 rounded-full text-xs ${invoice.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {invoice.status || 'active'}
                                                </span>
                                                <span className="text-sm font-medium">₹{totals.totalAmount.toLocaleString()}</span>
                                                <button
                                                    onClick={() => handleEditClick(invoice)}
                                                    className="text-blue-400 hover:text-blue-600 p-1"
                                                    title="Edit"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Notification Banner */}
                <NotificationBanner />

                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Recurring Invoices Manager</h1>
                            <p className="mt-2 text-gray-600">
                                Enhanced recurring invoice management with automatic customer creation and Zoho Books integration
                            </p>
                            {currentOrganization && (
                                <p className="mt-1 text-sm text-teal-600">
                                    Organization: <strong>{currentOrganization.name}</strong> ({currentOrganization.organization_id})
                                </p>
                            )}
                        </div>
                        <div className="flex items-center space-x-4">
                            {syncProgress && (
                                <div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-lg">
                                    <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
                                    <span className="text-sm text-blue-700">
                                        {syncProgress.status} ({syncProgress.current}/{syncProgress.total})
                                    </span>
                                </div>
                            )}
                            <button
                                onClick={fetchRecurringInvoices}
                                disabled={loading || !isOrganizationReady}
                                className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                <span>Refresh</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Navigation Tabs */}
                <div className="border-b border-gray-200 mb-6">
                    <nav className="-mb-px flex space-x-8">
                        {[
                            { id: 'overview', name: 'Overview', icon: BarChart3 },
                            { id: 'manage', name: 'Manage Invoices', icon: FileText },
                            ...(editingInvoice ? [{ id: 'edit', name: 'Edit Invoice', icon: Edit }] : [])
                        ].map(tab => {
                            const IconComponent = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                                        ? 'border-teal-500 text-teal-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    <IconComponent className="w-4 h-4" />
                                    <span>{tab.name}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="space-y-6">
                    {activeTab === 'overview' && <OverviewDashboard />}

                    {/* Manage Invoices Tab */}
                    {activeTab === 'manage' && (
                        <div className="space-y-6">
                            <OrganizationStatusBanner />

                            {isOrganizationReady && (
                                <>
                                    {/* Search and Filter Controls */}
                                    <div className="bg-white rounded-lg border p-6">
                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                                            <div className="flex-1 flex items-center space-x-4">
                                                <div className="relative">
                                                    <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search invoices..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                                    />
                                                </div>
                                                <select
                                                    value={filterStatus}
                                                    onChange={(e) => setFilterStatus(e.target.value)}
                                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                                >
                                                    <option value="all">All Status</option>
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                    <option value="synced">Synced to Zoho</option>
                                                    <option value="pending">Pending Sync</option>
                                                </select>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {selectedInvoices.length > 0 && (
                                                    <>
                                                        <span className="text-sm text-gray-600">
                                                            {selectedInvoices.length} selected
                                                        </span>
                                                        <button
                                                            onClick={() => syncRecurringInvoices()}
                                                            disabled={loading || !isOrganizationReady}
                                                            className="flex items-center space-x-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50"
                                                        >
                                                            <Send className="w-4 h-4" />
                                                            <span>Sync Selected</span>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Invoices Table */}
                                    <div className="bg-white rounded-lg border overflow-hidden">
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedInvoices(filteredInvoices.map(inv => inv.id));
                                                                    } else {
                                                                        setSelectedInvoices([]);
                                                                    }
                                                                }}
                                                                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                                            />
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Invoice Details
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Customer
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Frequency
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Amount
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Status
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Approval Status
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {loading ? (
                                                        <tr>
                                                            <td colSpan="7" className="px-6 py-4 text-center">
                                                                <div className="flex items-center justify-center space-x-2">
                                                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                                                    <span>Loading invoices...</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ) : filteredInvoices.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                                                                No recurring invoices found
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        filteredInvoices.map(invoice => {
                                                            const validation = validateRecurringInvoice(invoice);
                                                            const totals = calculateTotals(invoice.line_items || []);

                                                            return (
                                                                <tr key={invoice.id} className="hover:bg-gray-50">
                                                                    <td className="px-6 py-4">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedInvoices.includes(invoice.id)}
                                                                            onChange={(e) => {
                                                                                if (e.target.checked) {
                                                                                    setSelectedInvoices([...selectedInvoices, invoice.id]);
                                                                                } else {
                                                                                    setSelectedInvoices(selectedInvoices.filter(id => id !== invoice.id));
                                                                                }
                                                                            }}
                                                                            className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                                                        />
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div>
                                                                            <p className="text-sm font-medium text-gray-900">
                                                                                {invoice.recurrence_name || 'Untitled'}
                                                                            </p>
                                                                            <p className="text-sm text-gray-500">
                                                                                {invoice.invoice_subject || 'No subject'}
                                                                            </p>
                                                                            <div className="flex items-center mt-1">
                                                                                <div className={`w-2 h-2 rounded-full mr-2 ${invoice.zoho_recurring_invoice_id
                                                                                    ? 'bg-green-500'
                                                                                    : validation.isValid
                                                                                        ? 'bg-yellow-500'
                                                                                        : 'bg-red-500'
                                                                                    }`} />
                                                                                <span className="text-xs text-gray-400">
                                                                                    {invoice.zoho_recurring_invoice_id
                                                                                        ? 'Synced to Zoho'
                                                                                        : validation.isValid
                                                                                            ? 'Ready to sync'
                                                                                            : `${validation.completionPercentage}% complete`}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div>
                                                                            <p className="text-sm text-gray-900">{invoice.customer_name || 'No customer'}</p>
                                                                            <div className="flex items-center space-x-1 mt-1">
                                                                                {invoice.is_new_customer && (
                                                                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                                                                                        New Customer
                                                                                    </span>
                                                                                )}
                                                                                {invoice.customer_sync_status === 'synced' && (
                                                                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-teal-100 text-teal-800">
                                                                                        Customer Synced
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <p className="text-sm text-gray-900">
                                                                            Every {invoice.repeat_every || 1} {invoice.recurrence_frequency || 'month'}
                                                                        </p>
                                                                        <p className="text-sm text-gray-500">
                                                                            {invoice.start_date ? new Date(invoice.start_date).toLocaleDateString() : 'No start date'}
                                                                        </p>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <p className="text-sm font-medium text-gray-900">
                                                                            ₹{totals.totalAmount.toLocaleString()}
                                                                        </p>
                                                                        <p className="text-sm text-gray-500">
                                                                            Tax: ₹{totals.taxTotal.toLocaleString()}
                                                                        </p>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div className="flex flex-col space-y-1">
                                                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${invoice.status === 'active'
                                                                                ? 'bg-green-100 text-green-800'
                                                                                : 'bg-gray-100 text-gray-800'
                                                                                }`}>
                                                                                {invoice.status || 'Active'}
                                                                            </span>
                                                                            {invoice.zoho_recurring_invoice_id && (
                                                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-teal-100 text-teal-800">
                                                                                    Zoho Synced
                                                                                </span>
                                                                            )}
                                                                            {invoice.zoho_sync_status === 'failed' && (
                                                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                                                                    Sync Failed
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                                        {getApprovalStatusDisplay(invoice)}
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div className="flex items-center space-x-2">
                                                                            <button
                                                                                onClick={() => setViewingInvoice(invoice)}
                                                                                className="text-gray-400 hover:text-gray-600"
                                                                                title="View Details"
                                                                            >
                                                                                <Eye className="w-4 h-4" />
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleEditClick(invoice)}
                                                                                disabled={!canEdit(invoice) || !!invoice.zoho_recurring_invoice_id}
                                                                                className={`${!canEdit(invoice) || invoice.zoho_recurring_invoice_id
                                                                                    ? 'text-gray-400 cursor-not-allowed'
                                                                                    : 'text-blue-400 hover:text-blue-600'
                                                                                    }`}
                                                                                title={
                                                                                    invoice.zoho_recurring_invoice_id
                                                                                        ? 'Cannot edit synced recurring invoice'
                                                                                        : !canEdit(invoice)
                                                                                            ? 'Cannot edit - approval required'
                                                                                            : 'Edit'
                                                                                }
                                                                            >
                                                                                <Edit className="w-4 h-4" />
                                                                            </button>
                                                                            {validation.canSync && !invoice.zoho_recurring_invoice_id && (
                                                                                <button
                                                                                    onClick={() => syncRecurringInvoices([invoice.id])}
                                                                                    disabled={!canSync(invoice) || !!invoice.zoho_recurring_invoice_id}
                                                                                    className={`${!canSync(invoice) || invoice.zoho_recurring_invoice_id
                                                                                        ? 'text-gray-400 cursor-not-allowed'
                                                                                        : 'text-teal-400 hover:text-teal-600'
                                                                                        }`}
                                                                                    title={
                                                                                        invoice.zoho_recurring_invoice_id
                                                                                            ? 'Already synced to Zoho Books'
                                                                                            : !canSync(invoice)
                                                                                                ? 'Cannot sync - approval required'
                                                                                                : 'Sync to Zoho'
                                                                                    }
                                                                                >
                                                                                    <Send className="w-4 h-4" />
                                                                                </button>
                                                                            )}
                                                                            {/* Show disabled sync button for already synced invoices */}
                                                                            {invoice.zoho_recurring_invoice_id && (
                                                                                <button
                                                                                    disabled
                                                                                    className="text-gray-400 cursor-not-allowed"
                                                                                    title="Already synced to Zoho Books"
                                                                                >
                                                                                    <Send className="w-4 h-4" />
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Edit Invoice Tab */}
                    {activeTab === 'edit' && editingInvoice && (
                        <div className="bg-white rounded-lg border p-6">
                            <OrganizationStatusBanner />

                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Edit Recurring Invoice: {editingInvoice.recurrence_name}
                                </h2>
                                <button
                                    onClick={() => {
                                        setEditingInvoice(null);
                                        setActiveTab('manage');
                                    }}
                                    className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
                                >
                                    <X className="w-4 h-4" />
                                    <span>Cancel Edit</span>
                                </button>
                            </div>

                            {isOrganizationReady && (
                                <div className="space-y-6">
                                    {/* Customer Fields */}
                                    <CustomerCreationFields
                                        formData={recurringInvoiceForm}
                                        onChange={handleFormFieldChange}
                                        isNewCustomer={customerCreationMode}
                                    />

                                    {/* Invoice Basic Details */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Profile Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={recurringInvoiceForm.recurrence_name}
                                                onChange={(e) => handleFormFieldChange('recurrence_name', e.target.value)}
                                                placeholder="Monthly Service Fee"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Invoice Subject
                                            </label>
                                            <input
                                                type="text"
                                                value={recurringInvoiceForm.invoice_subject}
                                                onChange={(e) => handleFormFieldChange('invoice_subject', e.target.value)}
                                                placeholder="Invoice subject line"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>

                                    {/* Recurrence Settings */}
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <h3 className="text-lg font-medium text-gray-900 mb-4">Recurrence Schedule</h3>

                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Start Date *
                                                </label>
                                                <input
                                                    type="date"
                                                    value={recurringInvoiceForm.start_date}
                                                    onChange={(e) => handleFormFieldChange('start_date', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    End Date
                                                </label>
                                                <input
                                                    type="date"
                                                    value={recurringInvoiceForm.end_date}
                                                    onChange={(e) => handleFormFieldChange('end_date', e.target.value)}
                                                    disabled={recurringInvoiceForm.never_expires}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                                                />
                                                <div className="flex items-center mt-2">
                                                    <input
                                                        type="checkbox"
                                                        id="never_expires"
                                                        checked={recurringInvoiceForm.never_expires}
                                                        onChange={(e) => handleFormFieldChange('never_expires', e.target.checked)}
                                                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                                    />
                                                    <label htmlFor="never_expires" className="ml-2 text-sm text-gray-600">
                                                        Never expires
                                                    </label>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Frequency *
                                                </label>
                                                <select
                                                    value={recurringInvoiceForm.recurrence_frequency}
                                                    onChange={(e) => handleFormFieldChange('recurrence_frequency', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                                >
                                                    <option value="days">Days</option>
                                                    <option value="weeks">Weeks</option>
                                                    <option value="months">Months</option>
                                                    <option value="years">Years</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Repeat Every *
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={recurringInvoiceForm.repeat_every}
                                                    onChange={(e) => handleFormFieldChange('repeat_every', parseInt(e.target.value) || 1)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Payment Terms */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Payment Terms
                                            </label>
                                            <select
                                                value={recurringInvoiceForm.payment_terms}
                                                onChange={(e) => {
                                                    const value = parseInt(e.target.value);
                                                    const option = paymentTermsOptions.find(opt => opt.value === value);
                                                    handleFormFieldChange('payment_terms', value);
                                                    handleFormFieldChange('payment_terms_label', option?.label || '');
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                            >
                                                {paymentTermsOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Status
                                            </label>
                                            <select
                                                value={recurringInvoiceForm.status}
                                                onChange={(e) => handleFormFieldChange('status', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                            >
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Line Items */}
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-medium text-gray-900">Line Items</h3>
                                            <button
                                                type="button"
                                                onClick={addLineItem}
                                                className="flex items-center space-x-2 text-teal-600 hover:text-teal-700"
                                            >
                                                <Plus className="w-4 h-4" />
                                                <span>Add Item</span>
                                            </button>
                                        </div>

                                        <div className="space-y-4">
                                            {recurringInvoiceForm.line_items.map((item, index) => (
                                                <LineItemRow
                                                    key={index}
                                                    item={item}
                                                    index={index}
                                                    onItemChange={handleLineItemChange}
                                                    onZohoItemSelect={handleZohoItemSelect}
                                                    onTaxChange={handleTaxRateChange}
                                                    onRemove={removeLineItem}
                                                    canRemove={recurringInvoiceForm.line_items.length > 1}
                                                />
                                            ))}
                                        </div>

                                        {/* Totals */}
                                        <div className="mt-6 border-t pt-4">
                                            <div className="flex justify-end">
                                                <div className="w-64 space-y-2">
                                                    {(() => {
                                                        const totals = calculateTotals(recurringInvoiceForm.line_items);
                                                        return (
                                                            <>
                                                                <div className="flex justify-between">
                                                                    <span className="text-sm text-gray-600">Subtotal:</span>
                                                                    <span className="text-sm font-medium">₹{totals.subTotal.toLocaleString()}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-sm text-gray-600">Tax:</span>
                                                                    <span className="text-sm font-medium">₹{totals.taxTotal.toLocaleString()}</span>
                                                                </div>
                                                                <div className="flex justify-between border-t pt-2">
                                                                    <span className="font-medium">Total:</span>
                                                                    <span className="font-bold text-lg">₹{totals.totalAmount.toLocaleString()}</span>
                                                                </div>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notes and Terms */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Notes
                                            </label>
                                            <textarea
                                                value={recurringInvoiceForm.notes}
                                                onChange={(e) => handleFormFieldChange('notes', e.target.value)}
                                                placeholder="Invoice notes"
                                                rows={4}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Terms & Conditions
                                            </label>
                                            <textarea
                                                value={recurringInvoiceForm.terms}
                                                onChange={(e) => handleFormFieldChange('terms', e.target.value)}
                                                placeholder="Terms and conditions"
                                                rows={4}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>

                                    {/* Form Actions */}
                                    <div className="flex items-center justify-between pt-6 border-t">
                                        <div>
                                            {(() => {
                                                const validation = validateRecurringInvoice(recurringInvoiceForm);
                                                return (
                                                    <div className="flex items-center space-x-2">
                                                        <div className={`w-3 h-3 rounded-full ${validation.isValid ? 'bg-green-500' : 'bg-red-500'}`} />
                                                        <span className="text-sm text-gray-600">
                                                            Form completion: {validation.completionPercentage}%
                                                        </span>
                                                        {!validation.isValid && (
                                                            <span className="text-sm text-red-600">
                                                                ({validation.missingFields.length} missing fields)
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        <div className="flex items-center space-x-4">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditingInvoice(null);
                                                    setActiveTab('manage');
                                                }}
                                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={updateRecurringInvoice}
                                                disabled={loading || !isOrganizationReady}
                                                className="flex items-center space-x-2 bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50"
                                            >
                                                <Save className="w-4 h-4" />
                                                <span>Update Invoice</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* View Invoice Modal */}
            {viewingInvoice && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-gray-900">
                                {viewingInvoice.recurrence_name} - Invoice Details
                            </h3>
                            <button
                                onClick={() => setViewingInvoice(null)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Invoice Information</h4>
                                    <div className="space-y-2 text-sm">
                                        <div><span className="font-medium">Profile Name:</span> {viewingInvoice.recurrence_name}</div>
                                        <div><span className="font-medium">Subject:</span> {viewingInvoice.invoice_subject || 'No subject'}</div>
                                        <div><span className="font-medium">Customer:</span> {viewingInvoice.customer_name}</div>
                                        <div><span className="font-medium">Status:</span> {viewingInvoice.status}</div>
                                        {viewingInvoice.zoho_recurring_invoice_id && (
                                            <div><span className="font-medium">Zoho ID:</span> {viewingInvoice.zoho_recurring_invoice_id}</div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Recurrence Schedule</h4>
                                    <div className="space-y-2 text-sm">
                                        <div><span className="font-medium">Frequency:</span> Every {viewingInvoice.repeat_every} {viewingInvoice.recurrence_frequency}</div>
                                        <div><span className="font-medium">Start Date:</span> {viewingInvoice.start_date ? new Date(viewingInvoice.start_date).toLocaleDateString() : 'Not set'}</div>
                                        <div><span className="font-medium">End Date:</span> {viewingInvoice.end_date ? new Date(viewingInvoice.end_date).toLocaleDateString() : 'Never expires'}</div>
                                        <div><span className="font-medium">Payment Terms:</span> {viewingInvoice.payment_terms_label || 'Not set'}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Line Items */}
                            {viewingInvoice.line_items && viewingInvoice.line_items.length > 0 && (
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Line Items</h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {viewingInvoice.line_items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="px-4 py-3 text-sm text-gray-900">{item.description || item.item_name}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">{item.quantity}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">₹{item.rate}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">{item.tax_rate || 0}%</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">₹{((item.quantity || 0) * (item.rate || 0)).toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="mt-4 flex justify-end">
                                        <div className="w-64 space-y-2">
                                            {(() => {
                                                const totals = calculateTotals(viewingInvoice.line_items);
                                                return (
                                                    <>
                                                        <div className="flex justify-between text-sm">
                                                            <span>Subtotal:</span>
                                                            <span>₹{totals.subTotal.toLocaleString()}</span>
                                                        </div>
                                                        <div className="flex justify-between text-sm">
                                                            <span>Tax:</span>
                                                            <span>₹{totals.taxTotal.toLocaleString()}</span>
                                                        </div>
                                                        <div className="flex justify-between font-bold border-t pt-2">
                                                            <span>Total:</span>
                                                            <span>₹{totals.totalAmount.toLocaleString()}</span>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Notes and Terms */}
                            {(viewingInvoice.notes || viewingInvoice.terms) && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {viewingInvoice.notes && (
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                                            <p className="text-sm text-gray-700">{viewingInvoice.notes}</p>
                                        </div>
                                    )}
                                    {viewingInvoice.terms && (
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-2">Terms & Conditions</h4>
                                            <p className="text-sm text-gray-700">{viewingInvoice.terms}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setViewingInvoice(null)}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecurringInvoicesIntegrationManager;